import { useEffect, useRef, useState, useCallback } from "react";
import { T, base } from "../lib/theme";
import { apiFetch } from "../lib/api";

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1)  return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

const BellIcon = ({ color, size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
    <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
  </svg>
);

export default function NotificationBell({ token, socket }) {
  const [open,          setOpen]          = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unread,        setUnread]        = useState(0);
  const panelRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const nd = await apiFetch("/api/notifications", {}, token);
      if (nd.notifications) setNotifications(nd.notifications);
      if (typeof nd.unreadCount === "number") setUnread(nd.unreadCount);
    } catch {}
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // Real-time: receive new notifications via socket
  useEffect(() => {
    if (!socket) return;
    const handler = (data) => {
      if (data.notification) {
        setNotifications(prev => [data.notification, ...prev].slice(0, 50));
      }
      if (typeof data.unreadCount === "number") setUnread(data.unreadCount);
      else setUnread(u => u + 1);
    };
    socket.on("todo_reminder", handler);
    return () => socket.off("todo_reminder", handler);
  }, [socket]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const markAllRead = async () => {
    await apiFetch("/api/notifications/read-all", { method: "PATCH" }, token);
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnread(0);
  };

  const markRead = async (id) => {
    await apiFetch(`/api/notifications/${id}/read`, { method: "PATCH" }, token);
    setNotifications(prev => prev.map(n => n._id === id ? { ...n, read: true } : n));
    setUnread(u => Math.max(0, u - 1));
  };

  const clearAll = async () => {
    await apiFetch("/api/notifications/clear", { method: "DELETE" }, token);
    setNotifications([]);
    setUnread(0);
  };

  const handleOpen = () => {
    setOpen(v => !v);
  };

  return (
    <div ref={panelRef} style={{ position: "relative" }}>
      {/* Bell button */}
      <button
        onClick={handleOpen}
        style={{
          width: 36, height: 36, borderRadius: 10, cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
          background: open ? T.indigoDim : T.bg2,
          border: `1px solid ${open ? T.indigo : T.border1}`,
          position: "relative", transition: "all 0.15s",
        }}
        title="Notifications"
      >
        <BellIcon color={open ? T.indigo : T.text3} size={16} />
        {unread > 0 && (
          <span style={{
            position: "absolute", top: -4, right: -4,
            minWidth: 17, height: 17, borderRadius: 9,
            background: "#f43f5e", color: "#fff",
            fontSize: 10, fontWeight: 800, display: "flex",
            alignItems: "center", justifyContent: "center",
            padding: "0 4px", fontFamily: base.font,
            border: `2px solid ${T.bg1}`,
          }}>
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {/* Dropdown panel */}
      {open && (
        <div style={{
          position: "absolute", top: 44, right: 0, zIndex: 9999,
          width: 340, background: T.bg1,
          border: `1px solid ${T.border2}`,
          borderRadius: 14, overflow: "hidden",
          boxShadow: "0 8px 40px rgba(0,0,0,0.45)",
          animation: "fadeUp 0.18s ease",
        }}>
          {/* Header + tabs */}
          <div style={{ padding: "14px 16px 0", borderBottom: `1px solid ${T.border1}` }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: T.text1 }}>Notifications</span>
              {notifications.length > 0 && (
                <div style={{ display: "flex", gap: 8 }}>
                  {unread > 0 && (
                    <button onClick={markAllRead} style={{ fontSize: 11, color: T.indigo, background: "none", border: "none", cursor: "pointer", fontFamily: base.font, fontWeight: 600 }}>
                      Mark all read
                    </button>
                  )}
                  <button onClick={clearAll} style={{ fontSize: 11, color: T.text3, background: "none", border: "none", cursor: "pointer", fontFamily: base.font }}>
                    Clear all
                  </button>
                </div>
              )}
            </div>

            {/* Inbox tab only */}
            <div style={{ display: "flex" }}>
              <div style={{
                padding: "6px 14px", fontSize: 12, fontWeight: 700, color: T.indigo,
                borderBottom: `2px solid ${T.indigo}`, marginBottom: -1,
                display: "flex", alignItems: "center", gap: 5,
              }}>
                Inbox
                {unread > 0 && (
                  <span style={{ fontSize: 10, fontWeight: 700, padding: "1px 5px",
                    borderRadius: 5, background: "#f43f5e", color: "#fff" }}>
                    {unread}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Inbox */}
          <div style={{ maxHeight: 360, overflowY: "auto" }}>
            {notifications.length === 0 ? (
              <div style={{ padding: "40px 0", textAlign: "center" }}>
                <BellIcon color={T.text3} size={24} />
                <div style={{ fontSize: 13, color: T.text3, marginTop: 10, fontWeight: 500 }}>No notifications yet</div>
                <div style={{ fontSize: 11, color: T.text3, marginTop: 4 }}>Task reminders will appear here</div>
              </div>
            ) : (
              notifications.map(n => (
                <div key={n._id}
                  onClick={() => !n.read && markRead(n._id)}
                  style={{
                    display: "flex", gap: 10, padding: "12px 16px",
                    borderBottom: `1px solid ${T.border1}`,
                    background: n.read ? "transparent" : T.indigoDim,
                    cursor: n.read ? "default" : "pointer",
                    transition: "background 0.15s",
                  }}
                >
                  <div style={{
                    width: 34, height: 34, borderRadius: 9, flexShrink: 0,
                    background: n.type === "reminder" ? "rgba(245,158,11,0.15)" : T.bg2,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                      stroke={n.type === "reminder" ? "#f59e0b" : T.indigo} strokeWidth="1.8">
                      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                      <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                    </svg>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: n.read ? 400 : 600, color: T.text1, lineHeight: 1.4 }}>
                      {n.title}
                    </div>
                    {n.body && (
                      <div style={{ fontSize: 11, color: T.text3, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {n.body}
                      </div>
                    )}
                    <div style={{ fontSize: 10, color: T.text3, marginTop: 3 }}>{timeAgo(n.createdAt)}</div>
                  </div>
                  {!n.read && (
                    <div style={{ width: 7, height: 7, borderRadius: "50%", background: T.indigo, flexShrink: 0, marginTop: 4 }} />
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
